import { BanknotesIcon, BuildingOfficeIcon, ChartPieIcon, CubeTransparentIcon, DocumentTextIcon, CurrencyEuroIcon, GiftIcon, KeyIcon, UserGroupIcon, UsersIcon } from '@heroicons/react/24/outline';
import { HeroIcon } from '../../../types';
import { Permissions, ReferralRole } from '../../../types/graphqlTypes';
import { isPartnerRole, ordersForCustomers } from '../referrals/partnerRoles';

// A customer's referrals, among the admin pages. A partner gets them as a block of its own (see PARTNER_PAGES).
export const REFERRALS_PAGE = { path: 'referrals', icon: GiftIcon, label: 'referral program', permissions: [Permissions.Billing] } as const;

/** The organisation-admin pages, in sidebar order, each with the permissions that unlock it (mirrors the routes in App.tsx). */
export const ADMIN_PAGES = [
  { path: 'users', icon: UsersIcon, label: 'users and guests', permissions: [Permissions.Users] },
  { path: 'company-info', icon: BuildingOfficeIcon, label: 'organisation', permissions: [Permissions.Billing] },
  { path: 'subscription', icon: BanknotesIcon, label: 'subscription', permissions: [Permissions.Billing] },
  REFERRALS_PAGE,
  { path: 'api-keys', icon: KeyIcon, label: 'API keys', permissions: [Permissions.ApiKey] },
] as const;

/**
 * The object library: restricted like an admin page, but listed with the projects (where people look for it),
 * not in the admin block.
 */
export const OBJECT_LIBRARY_PAGE = { path: 'object-library', icon: CubeTransparentIcon, label: 'object library', permissions: [Permissions.ObjectLibrary] } as const;

/** The print templates: listed with the projects too, next to the object library. */
export const TEMPLATE_LIBRARY_PAGE = { path: 'templates', icon: DocumentTextIcon, label: 'print templates', permissions: [Permissions.TemplateLibrary] } as const;

/** Every permission-gated organisation page (switching organisations keeps you on one when allowed). */
const ORGANISATION_PAGES = [...ADMIN_PAGES, OBJECT_LIBRARY_PAGE, TEMPLATE_LIBRARY_PAGE] as const;

export type AdminPage = (typeof ORGANISATION_PAGES)[number];

/** Whether a user with these permissions on an organisation (or a super admin) may open the page there. */
export const canAccessAdminPage = (page: AdminPage, myPermissions: readonly Permissions[], isSuperAdmin: boolean | null | undefined): boolean =>
  !!isSuperAdmin || myPermissions.includes(Permissions.All) || page.permissions.some((permission) => myPermissions.includes(permission));

/** The admin page a `/organisations/:organisationId/<page>` pathname is on, if any. */
export const adminPageFromPathname = (pathname: string): AdminPage | undefined => {
  const segment = pathname.split('/')[3];
  return ORGANISATION_PAGES.find((page) => page.path === segment);
};

/** A partner's pages, in sidebar order, each its own item, for the roles that have it. They open with the same
 * permissions as the referrals admin page, which a customer gets among the admin pages instead. */
// `ordersLabel`: the label for a reseller or full-service partner, when it differs (its orders, a lead generator's sales).
export const PARTNER_PAGES: { path: string; icon: HeroIcon; label: string; ordersLabel?: string; forRole: (role: ReferralRole | undefined) => boolean }[] = [
  { path: 'referrals', icon: ChartPieIcon, label: 'overview', forRole: isPartnerRole },
  { path: 'referrals/customers', icon: UserGroupIcon, label: 'customers', forRole: ordersForCustomers },
  { path: 'referrals/sales', icon: CurrencyEuroIcon, label: 'sales', ordersLabel: 'orders', forRole: isPartnerRole },
];
